import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { unlinkTokensApiErrors } from './dto/unlink-tokens-api-errors'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

type UseUnlinkTokensHookProps = {
  installationId: number
  onSuccess: () => void
  onClose: () => void
}

interface UseUnlinkTokensHook {
  isLoading: boolean
  unlinkTokens: () => void
}

/**
 * Function for unlink all tokens from all wallboxes
 * @param installationId - unique installation identifier as a number
 * @param onSuccess - action function when is success
 * @param onClose - action function to close modal after success
 * @returns isLoading as boolean
 * @returns mutate function from react-query
 */
export const useUnlinkTokens = ({ installationId, onSuccess, onClose }: UseUnlinkTokensHookProps): UseUnlinkTokensHook => {
  const { t } = useCustomTranslation()
  const queryClient = useQueryClient()
  const authFetch = useAuthFetchWithResult()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const unlinkTokensApi = async (): Promise<ApiResult<boolean>> => {
    const unlinkTokensResponse = authFetch<boolean>({
      path: `tokens/installation/${installationId}/unassign-all-wallboxes`,
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'PUT',
    })

    return new ApiResult(await unlinkTokensResponse)
  }

  const { isPending, mutate: unlinkTokens } = usePortalMutation({
    mutationFn: unlinkTokensApi,
    onError: error => {
      if (axios.isAxiosError(error)) {
        enqueueErrorStack(t(unlinkTokensApiErrors[error.response?.data.errorCode]))
      } else {
        enqueueErrorStack(t('emobility.chargingCards.unlink-token-failed'))
      }
    },
    onSuccess: () => {
      enqueueSuccessStack(t('emobility.chargingCards.unlink-token-success'))
      queryClient.invalidateQueries({ queryKey: ['chargingCards', installationId] })
      queryClient.invalidateQueries({ queryKey: ['wallboxes', installationId] })
      onSuccess()
      onClose()
    },
  })

  return {
    unlinkTokens,
    isLoading: isPending,
  }
}
